/**
 * Milestone Detail Page - Deep dive into milestone progress
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Task, Priority } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle2, Clock, AlertCircle, TrendingUp, Calendar, Users } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { PageContainer } from '../components/PageContainer';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';

export default function MilestoneDetail() {
  const { milestoneId } = useParams<{ milestoneId: string }>();
  const navigate = useNavigate();
  const { state, loading, currentDate } = useApp();
  const { tasks, users } = useOrganizationData(state) as {
    tasks: Task[];
    users: any[];
  };

  if (loading || !state) {
    return <div className="p-8 text-center">Loading milestone details...</div>;
  }

  if (!state.currentUser) {
    return <div className="p-8 text-center">User context not loaded</div>;
  }

  const milestone = milestoneId as Priority;
  const milestoneTasks = tasks.filter((t) => t.priority === milestone);

  // Group tasks by status
  const tasksByStatus = {
    completed: milestoneTasks.filter((t) => t.status === 'Completed' || String(t.status) === 'Done'),
    inProgress: milestoneTasks.filter((t) => t.status === 'In Progress' || t.status === 'Active'),
    blocked: milestoneTasks.filter((t) => t.status === 'Blocked'),
    notStarted: milestoneTasks.filter((t) => t.status === 'Not Started'),
    other: milestoneTasks.filter(
      (t) =>
        t.status !== 'Done' &&
        t.status !== 'Completed' &&
        t.status !== 'In Progress' &&
        t.status !== 'Active' &&
        t.status !== 'Blocked' &&
        t.status !== 'Not Started'
    ),
  };

  // Calculate burndown data
  const totalEstimated = milestoneTasks.reduce((sum, t) => sum + (t.estimatedHours || 0), 0);
  const totalLogged = milestoneTasks.reduce(
    (sum, t) => sum + (t.timeLogs?.reduce((s, log) => s + log.hours, 0) || 0),
    0
  );
  const remainingWork = milestoneTasks
    .filter((t) => t.status !== 'Completed' && String(t.status) !== 'Done')
    .reduce((sum, t) => {
      const logged = t.timeLogs?.reduce((s, log) => s + log.hours, 0) || 0;
      return sum + Math.max(0, (t.estimatedHours || 0) - logged);
    }, 0);

  // Get timeline data (last 30 days)
  const timelineData = generateTimelineData(milestoneTasks);

  return (
    <PageContainer>
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            size="sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>

          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                Milestone: <span className="text-primary">{milestone}</span>
              </h1>
              <p className="text-muted-foreground">
                {milestoneTasks.length} tasks • {tasksByStatus.completed.length} completed
              </p>
            </div>
            <Badge className="text-2xl px-6 py-3">{milestone}</Badge>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overall Progress */}
            <Card>
              <CardHeader>
                <CardTitle>Overall Progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Completion</span>
                    <span className="font-semibold">
                      {milestoneTasks.length > 0
                        ? ((tasksByStatus.completed.length / milestoneTasks.length) * 100).toFixed(0)
                        : 0}
                      %
                    </span>
                  </div>
                  <Progress
                    value={
                      milestoneTasks.length > 0
                        ? (tasksByStatus.completed.length / milestoneTasks.length) * 100
                        : 0
                    }
                    className="h-4"
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <MetricCard
                    icon={<CheckCircle2 className="w-5 h-5" />}
                    label="Completed"
                    value={tasksByStatus.completed.length}
                    color="green"
                  />
                  <MetricCard
                    icon={<TrendingUp className="w-5 h-5" />}
                    label="In Progress"
                    value={tasksByStatus.inProgress.length}
                    color="blue"
                  />
                  <MetricCard
                    icon={<Clock className="w-5 h-5" />}
                    label="Not Started"
                    value={tasksByStatus.notStarted.length}
                    color="gray"
                  />
                  <MetricCard
                    icon={<AlertCircle className="w-5 h-5" />}
                    label="Blocked"
                    value={tasksByStatus.blocked.length}
                    color="red"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Burndown Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Burndown Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-primary/10 rounded-lg">
                      <div className="text-2xl font-bold text-primary">
                        {totalEstimated.toFixed(0)}h
                      </div>
                      <div className="text-xs text-primary">Total Estimated</div>
                    </div>
                    <div className="text-center p-4 bg-chart-2/10 rounded-lg">
                      <div className="text-2xl font-bold text-chart-2">
                        {totalLogged.toFixed(0)}h
                      </div>
                      <div className="text-xs text-chart-2">Hours Logged</div>
                    </div>
                    <div className="text-center p-4 bg-chart-5/10 rounded-lg">
                      <div className="text-2xl font-bold text-chart-5">
                        {remainingWork.toFixed(0)}h
                      </div>
                      <div className="text-xs text-chart-5">Remaining Work</div>
                    </div>
                  </div>

                  {/* Burndown timeline visualization */}
                  <div className="max-h-56 space-y-2 overflow-y-auto rounded-lg bg-muted/40 p-4">
                    {timelineData.map((day, idx) => {
                      const maxRemaining = Math.max(...timelineData.map((entry) => entry.remaining));
                      const percentRemaining = maxRemaining > 0 ? (day.remaining / maxRemaining) * 100 : 0;

                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{day.date}</span>
                            <span>{day.remaining.toFixed(0)}h</span>
                          </div>
                          <Progress
                            value={percentRemaining}
                            className="h-2 [&::-webkit-progress-value]:bg-primary/10 [&::-moz-progress-bar]:bg-primary/10"
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{timelineData[0]?.date || 'Start'}</span>
                    <span>Today</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tasks by Status */}
            <TaskStatusSection
              title="✅ Completed"
              tasks={tasksByStatus.completed}
              users={users}
              emptyMessage="No completed tasks yet"
            />
            <TaskStatusSection
              title="🚀 In Progress"
              tasks={tasksByStatus.inProgress}
              users={users}
              emptyMessage="No tasks in progress"
            />
            <TaskStatusSection
              title="🚫 Blocked"
              tasks={tasksByStatus.blocked}
              users={users}
              emptyMessage="No blocked tasks"
              highlight="red"
            />
            <TaskStatusSection
              title="⏸️ Not Started"
              tasks={tasksByStatus.notStarted}
              users={users}
              emptyMessage="All tasks have been started"
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Team Members */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Team Members
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TeamMemberList tasks={milestoneTasks} users={users} />
              </CardContent>
            </Card>

            {/* Risk Assessment */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Risk Assessment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {tasksByStatus.blocked.length > 0 && (
                  <div className="p-3 bg-destructive/10 border border-destructive/40 rounded">
                    <div className="font-semibold text-destructive mb-1">
                      🚨 {tasksByStatus.blocked.length} Blocked Tasks
                    </div>
                    <div className="text-destructive text-xs">
                      Requires immediate attention
                    </div>
                  </div>
                )}
                {totalLogged > totalEstimated * 1.1 && (
                  <div className="p-3 bg-chart-5/10 border border-chart-5/40 rounded">
                    <div className="font-semibold text-chart-5 mb-1">
                      ⚠️ Over Estimate
                    </div>
                    <div className="text-chart-5 text-xs">
                      {((totalLogged / totalEstimated - 1) * 100).toFixed(0)}% over original
                      estimate
                    </div>
                  </div>
                )}
                {tasksByStatus.notStarted.length > tasksByStatus.inProgress.length * 3 && (
                  <div className="p-3 bg-chart-5/10 border border-chart-5/40 rounded">
                    <div className="font-semibold text-chart-5 mb-1">
                      ⏰ Many Pending Tasks
                    </div>
                    <div className="text-chart-5 text-xs">
                      Consider parallelizing work
                    </div>
                  </div>
                )}
                {tasksByStatus.blocked.length === 0 &&
                  totalLogged <= totalEstimated * 1.1 &&
                  tasksByStatus.notStarted.length <= tasksByStatus.inProgress.length * 3 && (
                    <div className="p-3 bg-chart-2/10 border border-chart-2/40 rounded">
                      <div className="font-semibold text-chart-2 mb-1">
                        ✅ On Track
                      </div>
                      <div className="text-chart-2 text-xs">
                        No major risks identified
                      </div>
                    </div>
                  )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

interface TaskStatusSectionProps {
  title: string;
  tasks: Task[];
  users: any[];
  emptyMessage: string;
  highlight?: 'red' | 'green' | 'blue';
}

function TaskStatusSection({ title, tasks, users, emptyMessage, highlight }: TaskStatusSectionProps) {
  const navigate = useNavigate();

  if (tasks.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground italic">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  const highlightClasses = {
    red: 'border-destructive/40',
    green: 'border-chart-2/40',
    blue: 'border-primary/40',
  };

  return (
    <Card className={highlight ? highlightClasses[highlight] : ''}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{title}</span>
          <Badge variant="outline">{tasks.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {tasks.map((task) => {
            const assignees = users.filter((u) => task.assigneeIds.includes(u.id));
            const totalLogged = task.timeLogs?.reduce((sum, log) => sum + log.hours, 0) || 0;
            const progress =
              task.estimatedHours && task.estimatedHours > 0
                ? (totalLogged / task.estimatedHours) * 100
                : 0;

            return (
              <div
                key={task.id}
                onClick={() => navigate(`/task/${task.id}`)}
                className="p-3 border rounded-lg hover:border-primary/40 hover:shadow-md cursor-pointer transition-all"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="font-mono text-xs">
                        {task.id}
                      </Badge>
                    </div>
                    <h5 className="font-medium text-muted-foreground line-clamp-2">
                      {task.title}
                    </h5>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    {assignees.length > 0 && (
                      <span>
                        {assignees[0].name}
                        {assignees.length > 1 && ` +${assignees.length - 1}`}
                      </span>
                    )}
                    {task.estimatedHours && (
                      <span>
                        {totalLogged.toFixed(1)}/{task.estimatedHours}h
                      </span>
                    )}
                  </div>
                  {task.estimatedHours && task.estimatedHours > 0 && (
                    <span className={progress > 100 ? 'text-destructive' : ''}>
                      {progress.toFixed(0)}%
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'green' | 'blue' | 'gray' | 'red';
}

function MetricCard({ icon, label, value, color }: MetricCardProps) {
  const colorClasses = {
    green: 'bg-chart-2/10 text-chart-2',
    blue: 'bg-primary/10 text-primary',
    gray: 'bg-muted/40 text-muted-foreground',
    red: 'bg-destructive/10 text-destructive',
  };

  return (
    <div className={`p-4 rounded-lg ${colorClasses[color]}`}>
      <div className="mb-2">{icon}</div>
      <div className="text-2xl font-bold mb-1">{value}</div>
      <div className="text-xs opacity-75">{label}</div>
    </div>
  );
}

function TeamMemberList({ tasks, users }: { tasks: Task[]; users: any[] }) {
  const assigneeIds = new Set(tasks.flatMap((t) => t.assigneeIds));
  const activeAssignees = users.filter((u) => assigneeIds.has(u.id));

  return (
    <div className="space-y-3">
      {activeAssignees.map((user) => {
        const userTasks = tasks.filter((t) => t.assigneeIds.includes(user.id));
        const completed = userTasks.filter(
          (t) => t.status === 'Done' || t.status === 'Completed'
        ).length;
        const completionRate = userTasks.length > 0 ? (completed / userTasks.length) * 100 : 0;

        return (
          <div key={user.id} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/25 to-chart-4/10 flex items-center justify-center text-white font-semibold">
              {user.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{user.name}</div>
              <div className="text-xs text-muted-foreground">
                {completed}/{userTasks.length} tasks • {completionRate.toFixed(0)}%
              </div>
            </div>
          </div>
        );
      })}
      {activeAssignees.length === 0 && (
        <p className="text-sm text-muted-foreground italic">No team members assigned</p>
      )}
    </div>
  );
}

function generateTimelineData(tasks: Task[]): { date: string; remaining: number }[] {
  const days = 30;
  const today = new Date();
  const data: { date: string; remaining: number }[] = [];

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    // Calculate remaining work at that date
    let remaining = 0;
    tasks.forEach((task) => {
      if (task.status === 'Done' || task.status === 'Completed') {
        // Check if completed after this date
        const lastLog = task.timeLogs?.[task.timeLogs.length - 1];
        if (lastLog && new Date(lastLog.date) > date) {
          remaining += task.estimatedHours || 0;
        }
      } else {
        // Task is incomplete, add remaining hours
        const loggedUpToDate =
          task.timeLogs
            ?.filter((log) => new Date(log.date) <= date)
            .reduce((sum, log) => sum + log.hours, 0) || 0;
        remaining += Math.max(0, (task.estimatedHours || 0) - loggedUpToDate);
      }
    });

    data.push({ date: dateStr, remaining });
  }

  return data;
}
